/**
 * F45 (Wave 2D) — admin analytics dashboard home.
 *
 * Route: `/analytics`. Permission: `analytics:read` (all 4 admin roles — D4).
 *
 * Layout: privacy Banner + dual last-refreshed bar (daily + today) with a manual
 * refresh Button (rate-limited 1/5min, server-enforced) → period quick-select
 * Chips + DateRangePicker (custom) + compare-to-previous Switch → 12 KPI Stat grid
 * (PercentDelta when compare is on) → charts strip (Line DAU/WAU/MAU, Bar signups-
 * by-day, FunnelPanel booking, Donut sport-mix, CityIntensity, ActivityHeatmap) →
 * 5 Top-N Tables with deep-links to the scorecards.
 *
 * The rollup-backed panels serve the last worker snapshot; the banner makes that
 * explicit. AGGREGATES only — no PII (D15).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Chart,
  Chip,
  DateRangePicker,
  EmptyState,
  Skeleton,
  Stat,
  Switch,
  Table,
  useToast,
  type DateRange,
  type TableColumn,
} from '@bravemove/ui-web';
import {
  ANALYTICS_PERIODS,
  type AnalyticsDashboard,
  type AnalyticsKpiCard,
  type AnalyticsTopRow,
} from '@bravemove/types';
import { ApiError } from '../../auth/types';
import { adminAnalyticsApi } from '../../api/analytics';
import { PageHeader } from '../../components/PageHeader';
import { AnalyticsNav } from './AnalyticsNav';
import { FunnelPanel } from './components/FunnelPanel';
import { ActivityHeatmap } from './components/ActivityHeatmap';
import { CityIntensity } from './components/CityIntensity';
import { PercentDelta } from './components/PercentDelta';
import {
  defaultAnalyticsRange,
  deltaTone,
  formatRefreshTime,
  resolvePreset,
  roundPercent,
  type AnalyticsQuickPreset,
  type AnalyticsRangeValue,
} from './analyticsHelpers';

export function AnalyticsDashboardPage() {
  const { t, locale } = useI18n();
  const { push } = useToast();

  const [preset, setPreset] = useState<AnalyticsQuickPreset>('30d');
  const [range, setRange] = useState<AnalyticsRangeValue>(() => defaultAnalyticsRange());
  const [compare, setCompare] = useState(false);

  const [dashboard, setDashboard] = useState<AnalyticsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDashboard(
        await adminAnalyticsApi.getDashboard({ from: range.from, to: range.to, compare }),
      );
    } catch {
      setError(t('admin:analytics.error'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, compare, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDashboard();
  }, [fetchDashboard]);

  const onPreset = (value: AnalyticsQuickPreset) => {
    setPreset(value);
    const resolved = resolvePreset(value);
    if (resolved) setRange(resolved);
  };

  const onCustomRange = (dr: DateRange) => {
    setPreset('custom');
    setRange((prev) => ({ from: dr.start ?? prev.from, to: dr.end ?? prev.to }));
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await adminAnalyticsApi.refresh();
      push({ tone: 'success', title: t('admin:analytics.refreshed') });
      await fetchDashboard();
    } catch (err) {
      const apiErr = err as { status?: number; code?: string } | null;
      const status = err instanceof ApiError ? err.status : apiErr?.status;
      const code = err instanceof ApiError ? err.code : apiErr?.code;
      // The 1/5min limiter's 429 body carries the GLOBAL @fastify/rate-limit
      // plugin code RATE_LIMIT_EXCEEDED — no ANALYTICS_-specific code is ever
      // emitted by the server (the refresh-429-code-mismatch audit fix). The
      // legacy code is kept defensively in case a route-level builder lands.
      if (
        status === 429 ||
        code === 'RATE_LIMIT_EXCEEDED' ||
        code === 'ANALYTICS_REFRESH_RATE_LIMITED'
      ) {
        push({ tone: 'warning', title: t('admin:analytics.refreshRateLimited') });
      } else {
        push({ tone: 'danger', title: t('admin:analytics.error') });
      }
    } finally {
      setRefreshing(false);
    }
  };

  const dailyRefreshLabel = formatRefreshTime(dashboard?.lastDailyRefreshedAt ?? null, locale);
  const todayRefreshLabel = formatRefreshTime(dashboard?.lastTodayRefreshedAt ?? null, locale);

  return (
    <div className="space-y-6" data-testid="analytics-dashboard-page">
      <PageHeader
        eyebrow="Analytics"
        title={t('admin:analytics.title')}
        subtitle={t('admin:analytics.subtitle')}
      />

      <AnalyticsNav />

      {/* Privacy / aggregate-only disclaimer */}
      <div data-testid="analytics-privacy-banner">
        <Banner tone="info">{t('admin:analytics.privacyNote')}</Banner>
      </div>

      {/* Last-refreshed + manual refresh */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-hairline bg-surface px-4 py-3"
        data-testid="analytics-refresh-bar"
      >
        <div className="flex flex-col gap-0.5 text-[12.5px] text-ink-2">
          <span>
            {dailyRefreshLabel
              ? t('admin:analytics.lastRefreshed', { time: dailyRefreshLabel })
              : t('admin:analytics.lastRefreshedNever')}
          </span>
          {todayRefreshLabel ? (
            <span className="text-ink-3">
              {t('admin:analytics.lastTodayRefreshed', { time: todayRefreshLabel })}
            </span>
          ) : null}
        </div>
        <Button
          variant="secondary"
          size="sm"
          loading={refreshing}
          onClick={onRefresh}
          data-testid="analytics-refresh-button"
        >
          {refreshing ? t('admin:analytics.refreshing') : t('admin:analytics.refresh')}
        </Button>
      </div>

      {/* Period controls */}
      <div className="flex flex-col gap-3" data-testid="analytics-period-controls">
        <div className="flex flex-wrap items-center gap-2">
          {ANALYTICS_PERIODS.map((p) => (
            <Chip
              key={p}
              selected={preset === p}
              onClick={() => onPreset(p)}
              data-testid={`analytics-preset-${p}`}
            >
              {t(`admin:analytics.period.${p}`)}
            </Chip>
          ))}
          <Switch
            label={t('admin:analytics.period.compareToPrevious')}
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
            data-testid="analytics-compare-toggle"
          />
        </div>
        {preset === 'custom' ? (
          <DateRangePicker
            value={{ start: range.from, end: range.to }}
            onChange={onCustomRange}
            locale={locale}
          />
        ) : null}
      </div>

      {error ? (
        <div data-testid="analytics-error">
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}

      {loading || !dashboard ? (
        <div
          className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4"
          data-testid="analytics-loading"
        >
          {Array.from({ length: 12 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          {/* KPI grid */}
          <div
            className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4"
            data-testid="analytics-kpi-grid"
          >
            {dashboard.kpis.map((card) => (
              <KpiCard key={card.key} card={card} compare={compare} />
            ))}
          </div>

          {/* Charts strip */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title={t('admin:analytics.panels.activeUsersTrend')}>
              <Chart
                kind="line"
                data={dashboard.activeUsersTrend.map((p) => ({
                  day: p.day,
                  dau: p.dau,
                  wau: p.wau,
                  mau: p.mau,
                }))}
                xKey="day"
                showLegend
                series={[
                  { key: 'dau', label: t('admin:analytics.series.dau') },
                  { key: 'wau', label: t('admin:analytics.series.wau') },
                  { key: 'mau', label: t('admin:analytics.series.mau') },
                ]}
                height={240}
              />
            </ChartCard>
            <ChartCard title={t('admin:analytics.panels.signupsByDay')}>
              <Chart
                kind="bar"
                data={dashboard.signupsByDay.map((d) => ({ day: d.day, count: d.count }))}
                xKey="day"
                series={[{ key: 'count', label: t('admin:analytics.series.signups') }]}
                height={240}
              />
            </ChartCard>
            <ChartCard title={t('admin:analytics.panels.bookingFunnel')}>
              <FunnelPanel
                result={dashboard.bookingFunnelSummary}
                showTable={false}
                testId="analytics-dashboard-funnel"
              />
            </ChartCard>
            <ChartCard title={t('admin:analytics.panels.sportMix')}>
              <Chart
                kind="donut"
                data={dashboard.sportMix.map((s) => ({ sport: s.sport, value: s.count }))}
                xKey="sport"
                series={[{ key: 'value' }]}
                showLegend
                height={240}
              />
            </ChartCard>
            <ChartCard title={t('admin:analytics.panels.cityDistribution')}>
              <CityIntensity
                cities={dashboard.cityDistribution}
                ariaLabel={t('admin:analytics.panels.cityDistribution')}
              />
            </ChartCard>
            <ChartCard title={t('admin:analytics.panels.activityByHour')}>
              <ActivityHeatmap
                cells={dashboard.activityByHour}
                ariaLabel={t('admin:analytics.panels.activityByHour')}
              />
            </ChartCard>
          </div>

          {/* Top-N strip */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            <TopTable
              title={t('admin:analytics.panels.topVenues')}
              rows={dashboard.topVenues}
              testId="analytics-top-venues"
            />
            <TopTable
              title={t('admin:analytics.panels.topSports')}
              rows={dashboard.topSports}
              testId="analytics-top-sports"
            />
            <TopTable
              title={t('admin:analytics.panels.topTrainers')}
              rows={dashboard.topTrainers}
              testId="analytics-top-trainers"
            />
            <TopTable
              title={t('admin:analytics.panels.topCities')}
              rows={dashboard.topCities}
              testId="analytics-top-cities"
            />
            <TopTable
              title={t('admin:analytics.panels.topOpenGamePosters')}
              rows={dashboard.topOpenGamePosters}
              testId="analytics-top-open-game-posters"
            />
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ card, compare }: { card: AnalyticsKpiCard; compare: boolean }) {
  const { t } = useI18n();
  const showDelta = compare && card.deltaPercent !== undefined;
  // Percent KPIs render at 1-decimal precision — the backend emits raw floats.
  const value =
    card.unit === 'percent' ? `${roundPercent(card.value)}%` : card.value.toLocaleString();
  const delta: ReactNode = showDelta ? (
    <PercentDelta deltaPercent={card.deltaPercent!} label={t('admin:analytics.vsPrevious')} />
  ) : null;
  return (
    <div data-testid={`analytics-kpi-${card.key}`}>
      <Stat
        label={t(`admin:analytics.kpi.${card.key}`)}
        value={value}
        {...(delta ? { delta } : {})}
        deltaTone={showDelta ? deltaTone(card.deltaPercent!) : 'neutral'}
      />
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-hairline bg-surface p-4">
      <h2 className="mb-3 font-mono text-[11px] font-bold uppercase tracking-wider text-ink-3">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Map a Top-N row to its scorecard route (null for kinds without a scorecard). */
function scorecardPath(row: AnalyticsTopRow): string | null {
  if (row.entityKind === 'venue') return `/analytics/scorecards/venues/${row.entityId}`;
  if (row.entityKind === 'sport') return `/analytics/scorecards/sports/${row.entityId}`;
  if (row.entityKind === 'trainer') return `/analytics/scorecards/trainers/${row.entityId}`;
  return null;
}

function TopTable({
  title,
  rows,
  testId,
}: {
  title: string;
  rows: readonly AnalyticsTopRow[];
  testId: string;
}) {
  const { t } = useI18n();
  const columns = useMemo<readonly TableColumn<AnalyticsTopRow>[]>(
    () => [
      {
        key: 'name',
        header: title,
        cell: (row) => {
          const to = scorecardPath(row);
          return to ? (
            <Link
              to={to}
              className="text-[13px] font-semibold text-ink hover:underline"
              data-testid={`${testId}-link-${row.entityId}`}
            >
              {row.entityName}
            </Link>
          ) : (
            <span
              className="text-[13px] font-semibold text-ink"
              data-testid={`${testId}-name-${row.entityId}`}
            >
              {row.entityName}
            </span>
          );
        },
      },
      {
        key: 'value',
        header: t('admin:analytics.series.value'),
        align: 'end',
        cell: (row) => <span className="font-mono tabular-nums">{row.value.toLocaleString()}</span>,
      },
    ],
    [title, t, testId],
  );
  return (
    <div data-testid={testId}>
      {rows.length === 0 ? (
        <section className="rounded-xl border border-hairline bg-surface p-4">
          <h2 className="mb-3 font-mono text-[11px] font-bold uppercase tracking-wider text-ink-3">
            {title}
          </h2>
          <EmptyState title={t('admin:analytics.empty')} />
        </section>
      ) : (
        <Table
          data={rows as AnalyticsTopRow[]}
          columns={columns as TableColumn<AnalyticsTopRow>[]}
          rowKey={(row) => `${row.entityKind}:${row.entityId}`}
          density="compact"
        />
      )}
    </div>
  );
}

export default AnalyticsDashboardPage;
